/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import httpRequest from '@utils/http-request';
import { nanoid } from '@utils/crypto';

const API_BASE = import.meta.env.VITE_SERVER_ENDPOINT;

interface CredentialJSON {
    id: string;
    name: string;
    credential: string;
}

export type CredentialFieldType = 'secret' | 'totp';

export interface CredentialProtectedValue {
    type: CredentialFieldType;
    value: string;
    digits?: number;
    period?: number;
    algorithm?: 'SHA1' | 'SHA256' | 'SHA512';
}

export interface CredentialFieldDescription {
    title: string;
    description: string;
}

export interface CredentialField {
    id: string;
    protected: CredentialProtectedValue;
    descriptive: CredentialFieldDescription;
}

interface CredentialPayloadV3 {
    version: 3;
    fields: Array<{
        protected: CredentialProtectedValue;
        descriptive: {
            title: string;
            description?: string;
        };
    }>;
}

interface LegacyCredentialField {
    label: string;
    value: string;
    type?: 'text' | 'secret' | 'totp';
    digits?: number;
    period?: number;
    algorithm?: 'SHA1' | 'SHA256' | 'SHA512';
}

interface ParsedCredentialPayload {
    version?: unknown;
    fields?: unknown;
}

export function createCredentialField(
    title = '',
    value = '',
    type: CredentialFieldType = 'secret',
    description = ''
): CredentialField {
    return {
        id: nanoid(),
        protected: { type, value },
        descriptive: { title, description },
    };
}

export function serializeCredentialFields(fields: CredentialField[]): string {
    const payload: CredentialPayloadV3 = {
        version: 3,
        fields: fields.map((field) => {
            const description = field.descriptive.description.trim();
            return {
                protected: { ...field.protected },
                descriptive: {
                    title: field.descriptive.title.trim(),
                    ...(description ? { description } : {}),
                },
            };
        }),
    };
    return JSON.stringify(payload);
}

export function credentialFieldsAreComplete(fields: CredentialField[]): boolean {
    return (
        fields.length > 0 &&
        fields.every((field) => Boolean(field.descriptive.title.trim()) && Boolean(field.protected.value))
    );
}

export function parseCredentialFields(value: string): CredentialField[] {
    try {
        const payload = JSON.parse(value) as ParsedCredentialPayload;
        if (payload.version === 3 && Array.isArray(payload.fields)) {
            const fields = payload.fields
                .filter((field): field is CredentialPayloadV3['fields'][number] => {
                    if (!field || typeof field !== 'object') return false;
                    const candidate = field as Partial<CredentialPayloadV3['fields'][number]>;
                    return (
                        (candidate.protected?.type === 'secret' || candidate.protected?.type === 'totp') &&
                        typeof candidate.protected.value === 'string' &&
                        typeof candidate.descriptive?.title === 'string' &&
                        (candidate.descriptive.description === undefined ||
                            typeof candidate.descriptive.description === 'string')
                    );
                })
                .map((field) => ({
                    id: nanoid(),
                    protected: { ...field.protected },
                    descriptive: {
                        title: field.descriptive.title,
                        description: field.descriptive.description ?? '',
                    },
                }));
            if (fields.length > 0 || payload.fields.length === 0) return fields;
        }
        if ((payload.version === 1 || payload.version === 2) && Array.isArray(payload.fields)) {
            const fields = payload.fields
                .filter((field): field is LegacyCredentialField => {
                    if (!field || typeof field !== 'object') return false;
                    const candidate = field as Partial<LegacyCredentialField>;
                    return typeof candidate.label === 'string' && typeof candidate.value === 'string';
                })
                .map((field) => {
                    const migrated = createCredentialField(
                        field.label,
                        field.value,
                        field.type === 'totp' ? 'totp' : 'secret'
                    );
                    migrated.protected.digits = field.digits;
                    migrated.protected.period = field.period;
                    migrated.protected.algorithm = field.algorithm;
                    return migrated;
                });
            if (fields.length > 0 || payload.fields.length === 0) return fields;
        }
    } catch {
        // Legacy credentials were stored as a single decrypted string.
    }
    return [createCredentialField('Credential', value)];
}

export function credentialExists(credentials: Credential[] | null | undefined, id: string | null): boolean {
    return Array.isArray(credentials) && Boolean(id && credentials.some((credential) => credential.id === id));
}

export function removeCredential(
    credentials: Credential[] | null | undefined,
    id: string
): Credential[] | null | undefined {
    return Array.isArray(credentials) ? credentials.filter((credential) => credential.id !== id) : credentials;
}

export class Credential {
    id: string;
    name: string;
    credential: string;

    constructor(id: string, name: string, credential: string) {
        this.id = id;
        this.name = name;
        this.credential = credential;
    }

    static template = new Credential('', '', '');

    static fromJSON(json: Partial<CredentialJSON> | null | undefined): Credential {
        return new Credential(
            typeof json?.id === 'string' ? json.id : '',
            typeof json?.name === 'string' ? json.name : '',
            typeof json?.credential === 'string' ? json.credential : ''
        );
    }

    static fromJSONList(json: unknown): Credential[] {
        if (!Array.isArray(json)) return [];
        return json.map((item) => Credential.fromJSON(item as CredentialJSON));
    }

    static list = async (): Promise<Credential[]> => {
        const response = await httpRequest(`${API_BASE}/user/credentials`, 'GET'),
            json = await response.json();

        if (response.status === 200) {
            return Credential.fromJSONList(json.data);
        } else {
            throw new Error(json.message ?? json.error ?? 'Failed to load credentials');
        }
    };

    static get = async (id: string): Promise<Credential> => {
        const response = await httpRequest(`${API_BASE}/user/credentials/${id}`, 'GET'),
            json = await response.json();

        if (response.status === 200) {
            return Credential.fromJSON(json.data);
        } else {
            throw new Error(json.message ?? json.error ?? 'Credential not found');
        }
    };

    static save = async (id: string, credential: Partial<Credential>): Promise<Credential> => {
        const response = await httpRequest(`${API_BASE}/user/credentials/${id}`, 'PUT', credential),
            json = await response.json();

        if (response.status === 200) {
            return Credential.fromJSON(json.data);
        } else {
            throw new Error(json.message ?? json.error ?? 'Failed to save credential');
        }
    };

    static delete = async (id: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/user/credentials/${id}`, 'DELETE');

        if (response.status !== 200) {
            const json = await response.json();
            throw new Error(json.message ?? json.error ?? 'Failed to delete credential');
        }
    };
}
